import React, { useEffect, useRef } from "react";
import { ArrowLeft, ArrowRight, MapPin, Home, ChevronDown, Navigation, } from "lucide-react";
import Summary from "../components/Summary";
function LocationMapPreview({ location, type, }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  useEffect(() => {
    if (!window.google || !window.google.maps || !mapRef.current) {
      return;
    }
    const hasLocation = location && location.lat !== undefined && location.lng !== undefined;
    const position = hasLocation ? { lat: Number(location.lat), lng: Number(location.lng), } : { lat: 11.3410, lng: 77.7172, };
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(
        mapRef.current,
        {
          center: position,
          zoom: hasLocation ? 15 : 6,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          gestureHandling: "cooperative",
        }
      );
    } else {
      mapInstanceRef.current.setCenter(position);
      mapInstanceRef.current.setZoom(hasLocation ? 15 : 6);
    }
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }
    if (hasLocation) {
      markerRef.current = new window.google.maps.Marker({
        position, map: mapInstanceRef.current,
        title: location.address || `${type} Location`,
        label: { text: type === "Pickup" ? "P" : "D", color: "#ffffff", fontWeight: "700", },
      });
    }
  }, [location, type]);
  return (
    <div className="location-map-wrapper">
      <div ref={mapRef} className="location-map-preview" />
      <div className="map-overlay">
        <div className={"map-location-badge " + (type === "Pickup" ? "pickup" : "delivery")}>
          <MapPin size={14} />{type}</div>
        <div className="map-address">
          <strong>{location?.address || location?.name || `Select ${type.toLowerCase()} location`}</strong>
          {location?.lat &&
            location?.lng && (
              <small>
                {Number(location.lat).toFixed(5)}
                {" , "}
                {Number(location.lng).toFixed(5)}
              </small>
            )}
        </div>
      </div>
      {location?.lat &&
        location?.lng && (
          <button type="button" className="map-center-button"
            onClick={() => {
              if (mapInstanceRef.current) {
                mapInstanceRef.current.setCenter(
                  {
                    lat: Number(
                      location.lat
                    ),
                    lng: Number(location.lng),
                  });
                mapInstanceRef.current.setZoom(15);
              }
            }}>
            <Navigation size={14} />Center</button>
        )}
    </div>
  );
}
function LocationBox({ title, prefix, data, setData, }) {
  const isPickup = prefix === "from";
  const location = isPickup ? data.fromLocation : data.toLocation;
  const updateField = (key, value) => {
    setData((prev) => ({ ...prev, [key]: value, }));
  };
  return (
    <div className="location-card">
      <div className="location-card-header">
        <div className={"location-title-icon " + (isPickup ? "pickup" : "delivery")}>
          <MapPin size={20} />
        </div>
        <div>
          <h3>{title}</h3>
          <p>Where are we{" "}{isPickup ? "picking up" : "delivering"}{" "}your items?</p>
        </div>
      </div>
      <div className="location-tabs">
        <button type="button" className="selected"><MapPin size={14} />Search Location</button>
        <button type="button" onClick={() => { }}>Enter Address Manually</button>
      </div>
      <div className="location-search">
        <MapPin size={18} />
        <input type="text" placeholder={`Enter ${isPickup ? "pickup" : "delivery"} city, area or pincode`}
          value={data[prefix] || ""} onChange={(e) => updateField(prefix, e.target.value)} />
        {data[prefix] && (<span className="location-found">✓</span>)}
      </div>
      <LocationMapPreview location={location} type={isPickup ? "Pickup" : "Delivery"} />
      {location && (
        <div className="selected-location-info">
          <div className="selected-location-icon"><MapPin size={16} /></div>
          <div>
            <strong>Selected Location</strong>
            <span>{location.address || location.name || data[prefix]}</span>
          </div>
        </div>
      )}
      <div className="mini-fields">
        <label>Property Type
          <div className="input-icon select-wrapper">
            <Home size={17} />
            <select
              value={isPickup ? (data.pickupPropertyType || "2 BHK Apartment") : (data.deliveryPropertyType || "Individual House")}
              onChange={(e) => updateField(isPickup ? "pickupPropertyType" : "deliveryPropertyType", e.target.value)}>
              <option value="1 BHK Apartment">1 BHK Apartment</option>
              <option value="2 BHK Apartment">2 BHK Apartment</option>
              <option value="3 BHK Apartment">3 BHK Apartment</option>
              <option value="Individual House">Individual House</option>
              <option value="Villa">Villa</option>
              <option value="Office">Office</option>
              <option value="Shop">Shop</option>
            </select>
            <ChevronDown size={15} />
          </div>
        </label>
        <label>Floor Number
          <div className="input-icon">
            <Home size={17} />
            <input type="number" min="0" placeholder="Floor"
              value={isPickup ? (data.pickupFloor ?? 5) : (data.deliveryFloor ?? 2)}
              onChange={(e) => updateField(isPickup ? "pickupFloor" : "deliveryFloor", e.target.value)} />
          </div>
        </label>
        <label>Lift Available
          <div className="radio-line">
            <label>
              <input type="radio" name={`${prefix}-lift`}
                value="Yes" checked={(isPickup ? data.pickupLift : data.deliveryLift) !== "No"}
                onChange={() => updateField(isPickup ? "pickupLift" : "deliveryLift", "Yes")} />Yes</label>
            <label>
              <input type="radio" name={`${prefix}-lift`}
                value="No"
                checked={(isPickup ? data.pickupLift : data.deliveryLift) === "No"}
                onChange={() => updateField(isPickup ? "pickupLift" : "deliveryLift", "No")} />No</label>
          </div>
        </label>
        <label>Parking Available
          <div className="radio-line">
            <label>
              <input type="radio" name={`${prefix}-parking`}
                value="Yes" checked={(isPickup ? data.pickupParking : data.deliveryParking) !== "No"}
                onChange={() => updateField(isPickup ? "pickupParking" : "deliveryParking", "Yes")} />Yes</label>
            <label>
              <input type="radio" name={`${prefix}-parking`}
                value="No" checked={(isPickup ? data.pickupParking : data.deliveryParking) === "No"}
                onChange={() => updateField(isPickup ? "pickupParking" : "deliveryParking", "No")} />No</label>
          </div>
        </label>
      </div>
    </div>
  );
}
export default function Step3({ data, setData, next, back, }) {
  return (
    <div className="page-grid with-summary">
      <main className="form-card">
        <h2>3. Pickup & Delivery Details</h2>
        <p className="section-subtitle">Tell us where you're moving from and to. This helps us plan the best route, team and vehicle for your move.</p>
        <div className="location-grid">
          <LocationBox title="Pickup Location" prefix="from" data={data} setData={setData} />
          <LocationBox title="Delivery Location" prefix="to" data={data} setData={setData} />
        </div>
        <div className="actions">
          <button type="button" className="back ghost" onClick={back}><ArrowLeft size={17} />Back</button>
          <button type="button" className="primary" onClick={next}>Next: Additional Services<ArrowRight size={18} /></button>
        </div>
      </main>
      <Summary data={data} total={0} />
    </div>
  );
}